/**
 * InstagramChannelSetup.jsx — COM-01 Instagram DM Mock Channel Setup
 *
 * Mirrors FacebookChannelSetup. Provides:
 *   - Configuration form (IG Account ID, username, account type)
 *   - Validation with mock-mode gating notice
 *   - Activation blocked until instagram_manage_messages Meta App Review approval
 *
 * COM-01 contracts:
 *   - Always MOCK — never reaches ACTIVE in this phase
 *   - instagram_manage_messages requires Meta App Review (external dependency)
 *   - Only Instagram Business and Creator accounts are supported (not Personal)
 *   - Messages NEVER reach real Instagram recipients in mock mode
 */
import { useState, useEffect } from "react";
import axios from "axios";
import { Activity, AlertTriangle, CheckCircle2, XCircle, Info } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const STATUS_STEPS = ["UNCONFIGURED", "CONFIGURE", "VALIDATE", "ACTIVE"];

function StepIndicator({ status }) {
  const steps = [
    { id: "CONFIGURE", label: "Configure" },
    { id: "VALIDATE",  label: "Validate"  },
    { id: "ACTIVE",    label: "Activate"  },
  ];
  return (
    <div className="flex items-center gap-2">
      {steps.map((step, i) => {
        const done = STATUS_STEPS.indexOf(status) > STATUS_STEPS.indexOf(step.id);
        const active = step.id === status || (status === "UNCONFIGURED" && i === 0);
        return (
          <div key={step.id} className="flex items-center gap-2">
            <div className="flex items-center gap-1">
              <div
                className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold"
                style={{
                  background: done ? "#166534" : active ? "var(--brand-gold)" : "#E5E0D8",
                  color: done || active ? "#FFFFFF" : "#78716C",
                }}
              >
                {done ? "✓" : i + 1}
              </div>
              <span className="text-xs font-medium" style={{ color: done ? "#166534" : active ? "#1C1917" : "#78716C" }}>
                {step.label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div className="w-6 h-px" style={{ background: done ? "#166534" : "#E5E0D8" }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function InstagramChannelSetup() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [validating, setValidating] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [validationResult, setValidationResult] = useState(null);

  const [igAccountId, setIgAccountId] = useState("");
  const [igUsername, setIgUsername] = useState("");
  const [accountType, setAccountType] = useState("BUSINESS");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    axios.get(`${API}/comms/instagram/channel-config`, { withCredentials: true })
      .then(r => {
        setConfig(r.data);
        setIgAccountId(r.data.ig_account_id || "");
        setIgUsername(r.data.ig_username || "");
        setAccountType(r.data.account_type || "BUSINESS");
        setNotes(r.data.notes || "");
      })
      .catch(e => setError(e?.response?.data?.detail || "Failed to load Instagram config."))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setError(""); setSuccess("");
    try {
      const { data } = await axios.put(
        `${API}/comms/instagram/channel-config`,
        { ig_account_id: igAccountId, ig_username: igUsername, account_type: accountType, notes },
        { withCredentials: true }
      );
      setConfig(data);
      setSuccess("Instagram channel configuration saved.");
    } catch (e) {
      setError(e?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const handleValidate = async () => {
    setValidating(true);
    setError(""); setSuccess("");
    try {
      const { data } = await axios.post(
        `${API}/comms/instagram/channel-config/validate`,
        {},
        { withCredentials: true }
      );
      setValidationResult(data);
      setConfig(prev => prev ? { ...prev, status: data.status } : prev);
      if (data.status === "VALIDATE") {
        setSuccess("Configuration validated. Instagram messaging requires Meta App Review before activation.");
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Validation failed.");
    } finally {
      setValidating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-5 h-5 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: "var(--brand-gold)", borderTopColor: "transparent" }} />
      </div>
    );
  }

  const status = config?.status || "UNCONFIGURED";

  return (
    <div className="space-y-5" data-testid="instagram-channel-setup">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "#E1306C15" }}>
            <Activity className="w-5 h-5" style={{ color: "#E1306C" }} />
          </div>
          <div>
            <h3 className="text-sm font-bold" style={{ color: "#1C1917" }}>Instagram Direct Messages</h3>
            <p className="text-xs" style={{ color: "#78716C" }}>Receive and respond to Instagram Professional account DMs</p>
          </div>
        </div>
        <span
          data-testid="ig-mock-badge"
          className="text-[9px] font-bold px-2 py-1 rounded-full flex-shrink-0"
          style={{ background: "#FEF9C3", color: "#92400E", border: "1px solid #FDE047" }}
        >
          MOCK · SIMULATED
        </span>
      </div>

      <StepIndicator status={status} />

      {/* Mock notice */}
      <div
        className="flex gap-2 p-3 rounded-xl text-xs"
        style={{ background: "#FEF9C3", border: "1px solid #FDE047" }}
        data-testid="ig-mock-notice"
      >
        <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: "#92400E" }} />
        <div style={{ color: "#92400E" }}>
          <strong>MOCK Mode (COM-01):</strong> Instagram DMs are in simulated mode.
          Messages are stored in the mock outbox and never sent to real Instagram users.
          Production activation requires a Meta App Review approval for the
          <code className="mx-1 px-1 rounded text-[10px]" style={{ background: "#FDE047" }}>instagram_manage_messages</code>
          permission (external dependency — not controllable by the platform).
        </div>
      </div>

      {/* Form */}
      <div
        className="p-4 rounded-xl space-y-4"
        style={{ background: "#FFFFFF", border: "1px solid var(--brand-border)" }}
      >
        <p className="text-xs font-bold uppercase tracking-wider" style={{ color: "#78716C" }}>
          Channel Configuration
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium mb-1" style={{ color: "#57534E" }}>
              Instagram Account ID <span style={{ color: "#EF4444" }}>*</span>
            </label>
            <input
              data-testid="ig-account-id-input"
              type="text"
              value={igAccountId}
              onChange={e => setIgAccountId(e.target.value)}
              placeholder="e.g. 17841234567890"
              className="w-full px-3 py-2 rounded-lg text-sm border outline-none"
              style={{ border: "1px solid var(--brand-border)", color: "#1C1917" }}
            />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1" style={{ color: "#57534E" }}>
              @Username <span style={{ color: "#EF4444" }}>*</span>
            </label>
            <input
              data-testid="ig-username-input"
              type="text"
              value={igUsername}
              onChange={e => setIgUsername(e.target.value.replace(/^@/, ""))}
              placeholder="prashastaevents"
              className="w-full px-3 py-2 rounded-lg text-sm border outline-none"
              style={{ border: "1px solid var(--brand-border)", color: "#1C1917" }}
            />
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium mb-1" style={{ color: "#57534E" }}>
            Account Type <span style={{ color: "#EF4444" }}>*</span>
          </label>
          <div className="flex gap-2">
            {["BUSINESS", "CREATOR"].map(t => (
              <button
                key={t}
                data-testid={`ig-account-type-${t.toLowerCase()}`}
                onClick={() => setAccountType(t)}
                className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
                style={{
                  background: accountType === t ? "var(--brand-gold)" : "#F4F0EA",
                  color: accountType === t ? "#1C1917" : "#57534E",
                }}
              >
                {t}
              </button>
            ))}
          </div>
          <p className="text-[10px] mt-1" style={{ color: "#A8A29E" }}>
            Personal Instagram accounts are not supported by the Meta Messaging API.
          </p>
        </div>

        {error && (
          <div className="flex gap-1.5 text-xs" style={{ color: "#991B1B" }}>
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5" /> {error}
          </div>
        )}
        {success && (
          <div className="flex gap-1.5 text-xs" style={{ color: "#166534" }}>
            <CheckCircle2 className="w-3.5 h-3.5 mt-0.5" /> {success}
          </div>
        )}

        <div className="flex gap-2">
          <button
            data-testid="ig-save-btn"
            onClick={handleSave}
            disabled={saving || !igAccountId || !igUsername}
            className="px-4 py-2 rounded-lg text-xs font-semibold disabled:opacity-50"
            style={{ background: "var(--brand-gold)", color: "#1C1917" }}
          >
            {saving ? "Saving…" : "Save Configuration"}
          </button>
          {status === "CONFIGURE" && (
            <button
              data-testid="ig-validate-btn"
              onClick={handleValidate}
              disabled={validating || !igAccountId || !igUsername}
              className="px-4 py-2 rounded-lg text-xs font-semibold disabled:opacity-50"
              style={{ background: "#F4F0EA", color: "#57534E" }}
            >
              {validating ? "Validating…" : "Validate"}
            </button>
          )}
        </div>
      </div>

      {/* Validation results */}
      {validationResult && (
        <div
          className="p-4 rounded-xl space-y-2"
          style={{ background: "#F0FDF4", border: "1px solid #86EFAC" }}
          data-testid="ig-validation-results"
        >
          <p className="text-xs font-bold" style={{ color: "#166534" }}>Validation Results</p>
          {Object.entries(validationResult.checks || {}).map(([k, v]) => (
            <div key={k} className="flex items-center gap-2 text-xs">
              {v
                ? <CheckCircle2 className="w-3.5 h-3.5" style={{ color: "#166534" }} />
                : <XCircle className="w-3.5 h-3.5" style={{ color: "#9CA3AF" }} />
              }
              <span style={{ color: v ? "#166534" : "#6B7280" }}>{k.replace(/_/g, " ")}</span>
            </div>
          ))}
          {validationResult.activation_blocked_reason && (
            <p className="text-xs mt-2 font-medium" style={{ color: "#92400E" }}>
              {validationResult.activation_blocked_reason}
            </p>
          )}
        </div>
      )}

      {/* Production activation requirements */}
      <div
        className="p-3 rounded-xl text-xs"
        style={{ background: "#F4F0EA", border: "1px solid var(--brand-border)" }}
        data-testid="ig-activation-gate"
      >
        <strong style={{ color: "#1C1917" }}>Production Activation Requirements:</strong>
        <ul className="mt-1 space-y-0.5" style={{ color: "#57534E" }}>
          <li>• Instagram Business or Creator professional account</li>
          <li>• Connect Instagram account to a Facebook Page (required by Meta)</li>
          <li>• Create a Meta App at developers.facebook.com</li>
          <li>• Apply for <strong>instagram_manage_messages</strong> permission (requires Meta App Review)</li>
          <li>• Subscribe to Instagram Messaging webhook events</li>
          <li>• Store access_token in backend environment (never in frontend)</li>
        </ul>
      </div>
    </div>
  );
}
